'use client';

import { useState, useEffect } from 'react';
import { Box, Grid, TextField, CircularProgress, Button, MenuItem, Typography, InputAdornment } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';
import { formatUpperCase } from '../../../utils/formatTitleCase';
import PickerField from '../../../components/PickerField';
import PickerFieldTable from '../../../components/PickerFieldTable';
import usePickerPagination from '../../../hooks/usePickerPagination';

import { getTireReceiveById, updateTireReceive } from '../../../stores/tireReceiveStore';

import { useCompanyDataList } from '../../../stores/companyStore';
import { useTireSpecDataList } from '../../../stores/tireSpecStore';
import { useWarehouseDataList } from '../../../stores/warehouseStore';

const TireReceivingEdit = ({ open, id = null, onClose, onSuccess }) => {
  const notify = useNotifier();
  const { t } = useLanguage();

  const [initialValues, setInitialValues] = useState({
    company_id: '',
    batch_number: '',
    tire_brand_id: null,
    tire_brand_name: '',
    tire_size_id: null,
    tire_size_code: '',
    tire_size_code_internal: '',
    tire_spec_id: null,
    tire_spec_name: '',
    tire_spec_pattern: '',
    original_tread_depth: '',
    receive_date: dayjs().format('YYYY-MM-DD'),
    supplier: '',
    unit_price: '',
    condition: '',
    warehouse_id: null,
    warehouse_name: '',
    status: ''
  });

  const [loading, setLoading] = useState(false);
  const [serialNumbers, setSerialNumbers] = useState(['']);

  const handleSnChange = (index, value) => {
    const newSn = [...serialNumbers];
    newSn[index] = formatUpperCase(value);
    setSerialNumbers(newSn);
  };

  const conditionOptions = [
    { value: 'New', label: 'New' },
    { value: 'Retread', label: 'Retread' },
    { value: 'Used', label: 'Used' }
  ];

  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await getTireReceiveById(id);
        if (!mounted) return;
        if (data) {
          setInitialValues({
            company_id: (data.company ?? data.company_id) ? String(data.company ?? data.company_id) : '',
            batch_number: data.batch_number ?? data.name ?? data.serial_number ?? '',
            tire_brand_id: data.tire_brand ?? data.tire_brand_id ?? null,
            tire_brand_name: data.join?.tire_brand_name ?? data.tire_brand_name ?? '',
            tire_size_id: data.tire_size ?? data.tire_size_id ?? null,
            tire_size_code: data.join?.tire_size_code ?? data.tire_size_code ?? '',
            tire_size_code_internal: data.join?.tire_size_code_internal ?? data.tire_size_code_internal ?? '',
            tire_spec_id: data.tire_spec ?? data.tire_spec_id ?? null,
            tire_spec_name: data.join?.tire_spec_name ?? data.tire_spec_name ?? '',
            tire_spec_pattern:
              data.join?.tire_spec_pattern ?? data.join?.m_tire_spec_pattern ?? data.tire_spec_pattern ?? data.pattern ?? '',
            original_tread_depth:
              data.join?.tire_spec_original_tread_depth ?? data.join?.m_tire_spec_original_tread_depth ?? data.original_tread_depth ?? '',
            receive_date: data.receive_date ?? '',
            supplier: data.supplier ?? '',
            unit_price: (data.unit_price ?? data.total_price) ? Number(data.unit_price ?? data.total_price).toLocaleString('id-ID') : '',
            condition: data.current_condition ?? data.base_condition ?? data.condition ?? 'New',
            warehouse_id:
              data.warehouse && data.warehouse !== 0
                ? data.warehouse
                : data.warehouse_id && data.warehouse_id !== 0
                  ? data.warehouse_id
                  : null,
            warehouse_name: data.join?.warehouse_name ?? data.warehouse_name ?? '',
            status: data.status ?? ''
          });
          setSerialNumbers([data.serial_number || '']);
        }
      } catch (err) {
        console.error('Failed to load tire receiving:', err);
        notify({ message: t('tireReceiving.failedLoadData', 'Failed to load tire receiving data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, [id, open, notify, t]);

  const validationSchema = Yup.object().shape({
    company_id: Yup.string().required(t('validation.required', 'Required')),
    batch_number: Yup.string().required(t('validation.required', 'Required')),
    tire_spec_id: Yup.number().nullable().required(t('validation.required', 'Required')),
    receive_date: Yup.string().required(t('validation.required', 'Required')),
    supplier: Yup.string().required(t('validation.required', 'Required')),
    unit_price: Yup.string().required(t('validation.required', 'Required')),
    condition: Yup.string().required(t('validation.required', 'Required'))
  });

  const handleClose = () => {
    formik.resetForm();
    setSerialNumbers(['']);
    if (onClose) onClose();
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          batch_number: values.batch_number,
          company: Number(values.company_id),
          company_name: companyPickerParams.options.find((o) => String(o.id) === String(values.company_id))?.name || '',
          id: Number(id),
          is_active: true,
          license_plate: '',
          name: values.tire_spec_name,
          receive_date: values.receive_date ? dayjs(values.receive_date).format('YYYY-MM-DD HH:mm:ss') : '',
          serial_number: serialNumbers[0],
          status: values.status || '',
          supplier: values.supplier,
          tire_brand: Number(values.tire_brand_id),
          tire_brand_name: values.tire_brand_name,
          tire_size: Number(values.tire_size_id),
          tire_size_code: values.tire_size_code,
          tire_size_code_internal: values.tire_size_code_internal,
          tire_spec: Number(values.tire_spec_id),
          tire_spec_name: values.tire_spec_name,
          total_price: values.unit_price ? Number(String(values.unit_price).replace(/[^0-9]/g, '')) : 0,
          vehicle: 0,
          warehouse: Number(values.warehouse_id),
          warehouse_name: values.warehouse_name || ''
        };

        await updateTireReceive(id, payload);
        notify({ message: t('tireReceiving.successEdit', 'Tire Receive updated successfully'), severity: 'success' });

        formik.resetForm();
        setSerialNumbers(['']);
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.message || err?.response?.data?.error || 'Failed to save data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const tireSpecPickerParams = usePickerPagination({
    fetchHook: useTireSpecDataList,
    buildArgs: (state) => [
      state.page,
      state.pageSize,
      formik.values.company_id ? [{ value: formik.values.company_id, label: formik.values.company_id }] : [],
      state.searchKey,
      state.searchValue,
      []
    ],
    searchKey: 'spec_name',
    formatOption: (item) => {
      const sn = item.spec_name || '';
      const p = item.pattern || '';
      const b = item.join?.tire_brand_name || item.tire_brand_name || '';
      const s =
        item.join?.tire_size_code_internal || item.tire_size_code_internal || item.join?.tire_size_code || item.tire_size_code || '';
      const otd = item.original_tread_depth ? String(item.original_tread_depth) : '';
      const labelParts = [sn, b, s, p, otd].filter(Boolean).join(' - ');
      return {
        ...item,
        value: String(item.id),
        label: labelParts || String(item.id)
      };
    }
  });

  const warehousePickerParams = usePickerPagination({
    fetchHook: useWarehouseDataList,
    buildArgs: (state) => [
      state.page,
      state.pageSize,
      formik.values.company_id ? [{ value: formik.values.company_id, label: formik.values.company_id }] : [],
      state.searchKey,
      state.searchValue
    ],
    searchKey: 'name',
    formatOption: (item) => ({
      ...item,
      value: String(item.id),
      label: item.name || String(item.id)
    })
  });

  const dialogActions = (
    <>
      <Button onClick={handleClose} disabled={formik.isSubmitting} sx={{ color: 'text.secondary', textTransform: 'none' }}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{ textTransform: 'none', borderRadius: 1, px: 3, fontWeight: 700 }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
    </>
  );

  const specColumns = [
    { field: 'spec_name', headerName: t('specification.spec_name', 'Spec Name'), width: 200 },
    {
      field: 'brand_name',
      headerName: t('specification.brand', 'Brand'),
      width: 150,
      renderCell: (raw) => raw?.join?.tire_brand_name || raw?.join?.brand_name || raw?.tire_brand_name || raw?.brand_name || '-'
    },
    {
      field: 'size_code',
      headerName: t('specification.size', 'Size'),
      width: 150,
      renderCell: (raw) =>
        raw?.join?.tire_size_code_internal ||
        raw?.join?.size_code_internal ||
        raw?.join?.tire_size_code ||
        raw?.join?.size_code ||
        raw?.tire_size_code_internal ||
        raw?.size_code ||
        raw?.tire_size_code ||
        raw?.size_code_internal ||
        '-'
    },
    { field: 'pattern', headerName: t('specification.pattern', 'Pattern'), width: 150 },
    { field: 'original_tread_depth', headerName: t('specification.originalTreadDepth', 'OTD'), width: 100 }
  ];

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={t('general.edit', 'Edit') + ' ' + t('tireReceiving.title', 'Tire Receiving')}
      fullScreenMode={true}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', pt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2.5}>
              <Grid item size={{ xs: 12, md: 4 }}>
                <PickerField
                  label={t('general.company', 'Company')}
                  value={formik.values.company_id}
                  onChange={(val) => {
                    formik.setFieldValue('company_id', val ?? '');
                  }}
                  error={formik.touched.company_id && Boolean(formik.errors.company_id)}
                  helperText={formik.touched.company_id && formik.errors.company_id}
                  options={companyPickerParams.options}
                  loading={companyPickerParams.loading}
                  onSearch={companyPickerParams.handleSearch}
                  onScrollEnd={companyPickerParams.handleScrollEnd}
                  getOptionId={(o) => o?.value ?? o?.id ?? ''}
                  getOptionLabel={(o) => o?.label ?? o?.name ?? ''}
                  required
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    label={`${t('tireReceiving.receiveDate', 'Receive Date')} *`}
                    value={formik.values.receive_date ? dayjs(formik.values.receive_date) : null}
                    onChange={(newValue) => {
                      formik.setFieldValue('receive_date', newValue ? newValue.format('YYYY-MM-DD') : '');
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        id: 'receive_date',
                        name: 'receive_date',
                        error: formik.touched.receive_date && !!formik.errors.receive_date,
                        helperText: formik.touched.receive_date && formik.errors.receive_date
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  label={t('tireReceiving.batchNo', 'Batch Number')}
                  fullWidth
                  value={formik.values.batch_number}
                  error={formik.touched.batch_number && Boolean(formik.errors.batch_number)}
                  helperText={formik.touched.batch_number && formik.errors.batch_number}
                  disabled
                  slotProps={{
                    input: {
                      readOnly: true
                    }
                  }}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <PickerFieldTable
                  label={t('specification.name', 'Spec Name')}
                  dialogTitle={t('tireReceiving.selectSpec', 'Select Specification')}
                  value={formik.values.tire_spec_id}
                  onChange={(valId) => {
                    formik.setFieldValue('tire_spec_id', valId || null);
                    if (valId) {
                      const val = tireSpecPickerParams.options.find((o) => String(o.id) === String(valId));
                      if (val) {
                        formik.setFieldValue('tire_spec_name', val.spec_name || '');
                        formik.setFieldValue('tire_spec_pattern', val.pattern || '');

                        formik.setFieldValue('tire_brand_id', val.tire_brand || val.tire_brand_id || null);
                        formik.setFieldValue(
                          'tire_brand_name',
                          val.join?.tire_brand_name || val.join?.brand_name || val.tire_brand_name || val.brand_name || ''
                        );

                        formik.setFieldValue('tire_size_id', val.tire_size || val.tire_size_id || null);
                        formik.setFieldValue(
                          'tire_size_code',
                          val.join?.tire_size_code || val.join?.size_code || val.tire_size_code || val.size_code || ''
                        );

                        formik.setFieldValue(
                          'tire_size_code_internal',
                          val.join?.tire_size_code_internal ||
                            val.join?.size_code_internal ||
                            val.tire_size_code_internal ||
                            val.size_code_internal ||
                            ''
                        );
                        formik.setFieldValue('original_tread_depth', val.original_tread_depth ?? '');
                      }
                    } else {
                      formik.setFieldValue('tire_spec_name', '');
                      formik.setFieldValue('tire_spec_pattern', '');
                      formik.setFieldValue('tire_brand_id', null);
                      formik.setFieldValue('tire_brand_name', '');
                      formik.setFieldValue('tire_size_id', null);
                      formik.setFieldValue('tire_size_code', '');
                      formik.setFieldValue('tire_size_code_internal', '');
                      formik.setFieldValue('original_tread_depth', '');
                    }
                  }}
                  columns={specColumns}
                  {...tireSpecPickerParams}
                  getOptionId={(o) => o?.value ?? o?.id ?? ''}
                  getOptionLabel={(o) => o?.label ?? o?.pattern ?? o?.spec_name ?? ''}
                  disabled={!formik.values.company_id}
                  required
                  error={formik.touched.tire_spec_id && Boolean(formik.errors.tire_spec_id)}
                  helperText={formik.touched.tire_spec_id && formik.errors.tire_spec_id}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  label={t('specification.brand', 'Brand')}
                  value={formik.values.tire_brand_name || ''}
                  size="medium"
                  fullWidth
                  placeholder={!formik.values.tire_spec_id ? 'Please select a specification first' : ''}
                  slotProps={{
                    input: {
                      readOnly: true
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  helperText={t('tireReceiving.autoFillInfo', 'This field is auto-filled when you select a specification.')}
                  FormHelperTextProps={{ sx: { color: 'info.main' } }}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  label={t('specification.size', 'Size')}
                  value={formik.values.tire_size_code_internal || formik.values.tire_size_code || ''}
                  size="medium"
                  fullWidth
                  placeholder={!formik.values.tire_spec_id ? 'Please select a specification first' : ''}
                  slotProps={{
                    input: {
                      readOnly: true
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  helperText={t('tireReceiving.autoFillInfo', 'This field is auto-filled when you select a specification.')}
                  FormHelperTextProps={{ sx: { color: 'info.main' } }}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  label={t('specification.pattern', 'Pattern')}
                  value={formik.values.tire_spec_pattern || ''}
                  size="medium"
                  fullWidth
                  placeholder={!formik.values.tire_spec_id ? 'Please select a specification first' : ''}
                  slotProps={{
                    input: {
                      readOnly: true
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  helperText={t('tireReceiving.autoFillInfo', 'This field is auto-filled when you select a specification.')}
                  FormHelperTextProps={{ sx: { color: 'info.main' } }}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  label={t('specification.originalTreadDepth', 'Original Tread Depth (OTD)')}
                  value={formik.values.original_tread_depth || ''}
                  size="medium"
                  fullWidth
                  placeholder={!formik.values.tire_spec_id ? 'Please select a specification first' : ''}
                  slotProps={{
                    input: {
                      readOnly: true
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  helperText={t('tireReceiving.autoFillInfo', 'This field is auto-filled when you select a specification.')}
                  FormHelperTextProps={{ sx: { color: 'info.main' } }}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  label={t('tireReceiving.supplier', 'Supplier')}
                  name="supplier"
                  value={formik.values.supplier}
                  onChange={(e) => {
                    formik.setFieldValue('supplier', formatUpperCase(e.target.value));
                  }}
                  onBlur={formik.handleBlur}
                  size="medium"
                  fullWidth
                  required
                  error={formik.touched.supplier && Boolean(formik.errors.supplier)}
                  helperText={formik.touched.supplier && formik.errors.supplier}
                  InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } } }}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  label={t('tireReceiving.unitPrice', 'Purchase Price / Unit')}
                  name="unit_price"
                  value={formik.values.unit_price}
                  onChange={(e) => {
                    let val = e.target.value.replace(/[^0-9]/g, '');
                    if (val) {
                      val = Number(val).toLocaleString('id-ID');
                    }
                    formik.setFieldValue('unit_price', val);
                  }}
                  onBlur={formik.handleBlur}
                  size="medium"
                  fullWidth
                  required
                  error={formik.touched.unit_price && Boolean(formik.errors.unit_price)}
                  helperText={formik.touched.unit_price && formik.errors.unit_price}
                  InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">Rp</InputAdornment>
                  }}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  label={t('dashboardAsset.condition', 'Condition')}
                  name="condition"
                  value={formik.values.condition}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  size="medium"
                  fullWidth
                  required
                  error={formik.touched.condition && Boolean(formik.errors.condition)}
                  helperText={formik.touched.condition && formik.errors.condition}
                  InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } } }}
                >
                  {conditionOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <PickerField
                  label={t('warehouse.name', 'Warehouse')}
                  value={formik.values.warehouse_id}
                  onChange={(valId) => {
                    formik.setFieldValue('warehouse_id', valId || null);
                    if (valId) {
                      const val = warehousePickerParams.options.find((o) => String(o.id) === String(valId));
                      if (val) {
                        formik.setFieldValue('warehouse_name', val.name || '');
                      }
                    } else {
                      formik.setFieldValue('warehouse_name', '');
                    }
                  }}
                  options={warehousePickerParams.options}
                  loading={warehousePickerParams.loading}
                  onSearch={warehousePickerParams.handleSearch}
                  onScrollEnd={warehousePickerParams.handleScrollEnd}
                  getOptionId={(o) => o?.value ?? o?.id ?? ''}
                  getOptionLabel={(o) => o?.label ?? o?.name ?? ''}
                  disabled={!formik.values.company_id}
                  required
                  error={formik.touched.warehouse_id && Boolean(formik.errors.warehouse_id)}
                  helperText={formik.touched.warehouse_id && formik.errors.warehouse_id}
                />
              </Grid>
            </Grid>

            {/* Serial Number Section */}
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="600" color="text.primary" sx={{ letterSpacing: 0.2 }}>
                  {t('tyreItem.serialNumber', 'Serial Number')}
                </Typography>
              </Box>

              <Grid container spacing={2.5}>
                <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                    <Typography
                      variant="caption"
                      fontWeight="700"
                      sx={{ color: '#485D9A', letterSpacing: 0.8, textTransform: 'uppercase', fontSize: '0.7rem' }}
                    >
                      TIRE
                    </Typography>
                    <TextField
                      value={serialNumbers[0] || ''}
                      onChange={(e) => handleSnChange(0, e.target.value)}
                      size="small"
                      fullWidth
                      placeholder={`SN-1`}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          bgcolor: '#F8FAFC',
                          '& fieldset': {
                            borderColor: '#485D9A'
                          },
                          '&:hover fieldset': {
                            borderColor: '#485D9A'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                            borderWidth: '1.5px'
                          }
                        },
                        '& .MuiInputBase-input': {
                          py: 1,
                          color: 'text.main',
                          fontWeight: 500
                        }
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default TireReceivingEdit;
